/**
 * 響應式導航選單
 */

import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { CartButton } from '@/components/cart/CartButton';
import { 
  Menu, X, User, LogIn, LogOut, 
  ShoppingBag, Home, Package, Settings, Sparkles 
} from 'lucide-react';

export function Navigation({ activeTab, onTabChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // 檢查是否在首頁
  const isHomePage = location.pathname === '/';

  const handleLogout = async () => {
    await signOut();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { to: '/', label: '首頁', icon: Home },
    { to: '/product', label: 'Youth Secret', icon: Sparkles },
  ];
  
  // 首頁科學資訊頁籤
  const homeTabItems = [
    { id: 'home', label: '首頁' },
    { id: 'science', label: '科學基礎' },
    { id: 'evidence', label: '臨床實證' },
    { id: 'market', label: '市場與法規' },
    { id: 'conclusion', label: '結論與建議' },
  ];

  return (
    <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            onClick={() => isHomePage && onTabChange && onTabChange('home')}
            className="flex items-center gap-2 font-bold text-xl bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent hover:opacity-80 transition-opacity"
          >
            NMN 研究室
          </Link>

          {/* 桌機版導航 - 整合所有項目 */}
          <div className="hidden lg:flex items-center gap-1">
            {/* 首頁科學資訊頁籤（只在首頁顯示） */}
            {isHomePage && homeTabItems.map((item) => (
              <button
                key={item.id}
                onClick={() => onTabChange && onTabChange(item.id)}
                className={`
                  px-4 py-2 text-sm font-medium transition-all whitespace-nowrap
                  ${activeTab === item.id
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                  }
                `}
              >
                {item.label}
              </button>
            ))}
            
            {/* 一般導航連結（非首頁時顯示） */}
            {!isHomePage && navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors flex items-center gap-2 whitespace-nowrap"
              >
                <link.icon className="h-4 w-4" />
                {link.label}
              </Link>
            ))}
            
            {/* Youth Secret 按鈕（首頁時顯示） */}
            {isHomePage && (
              <Link
                to="/product"
                className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap border-l border-gray-200 ml-2"
              >
                <Sparkles className="h-4 w-4" />
                Youth Secret
              </Link>
            )}
          </div>

          {/* 右側按鈕組 */}
          <div className="flex items-center gap-4">
            {/* 購物車按鈕 */}
            <CartButton />

            {/* 會員/登入按鈕 - 桌機版 */}
            <div className="hidden md:flex items-center gap-2">
              {user ? (
                <>
                  {isAdmin() && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/admin')}
                      className="gap-2"
                    >
                      <Settings className="h-4 w-4" />
                      後台管理
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/member')}
                    className="gap-2"
                  >
                    <User className="h-4 w-4" />
                    {user.user_metadata?.name || '會員中心'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="gap-2 text-red-600 hover:text-red-700"
                  >
                    <LogOut className="h-4 w-4" />
                    登出
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/login')}
                    className="gap-2"
                  >
                    <LogIn className="h-4 w-4" />
                    登入
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => navigate('/register')}
                    className="bg-gradient-to-r from-blue-600 to-cyan-600 gap-2"
                  >
                    <User className="h-4 w-4" />
                    註冊
                  </Button>
                </>
              )}
            </div>

            {/* 手機版選單按鈕 */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* 手機版選單 */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white/95 backdrop-blur-xl animate-slide-down">
          <div className="container mx-auto px-4 py-4 space-y-2">
            {/* 導航連結 */}
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <link.icon className="h-5 w-5 text-gray-600" />
                <span className="font-medium text-gray-700">{link.label}</span>
              </Link>
            ))}

            <div className="border-t border-gray-200 my-2"></div>

            {/* 會員相關 */}
            {user ? (
              <>
                <button
                  onClick={() => {
                    navigate('/member');
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors w-full text-left"
                >
                  <User className="h-5 w-5 text-gray-600" />
                  <span className="font-medium text-gray-700">會員中心</span>
                </button>
                
                {isAdmin() && (
                  <button
                    onClick={() => {
                      navigate('/admin');
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors w-full text-left"
                  >
                    <Settings className="h-5 w-5 text-gray-600" />
                    <span className="font-medium text-gray-700">後台管理</span>
                  </button>
                )}

                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-red-50 transition-colors w-full text-left text-red-600"
                >
                  <LogOut className="h-5 w-5" />
                  <span className="font-medium">登出</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    navigate('/login');
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors w-full text-left"
                >
                  <LogIn className="h-5 w-5 text-gray-600" />
                  <span className="font-medium text-gray-700">登入</span>
                </button>
                <button
                  onClick={() => {
                    navigate('/register');
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 text-white transition-all hover:shadow-lg w-full text-left"
                >
                  <User className="h-5 w-5" />
                  <span className="font-medium">註冊</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
      `}</style>
    </nav>
  );
}

export default Navigation;
